import { useEffect, useState } from 'react';
import { Bell, BellRing, BellOff, Loader2, Smartphone, Check } from 'lucide-react';
import { toast } from 'sonner';
import {
  isPushSupported, isIOS, isStandalonePWA,
  subscribeForPush, getCurrentSubscription, unsubscribeFromPush,
} from '../utils/webPush';
import { isNativeApp, getNativePushStatus, requestNativePush } from '../lib/nativeBridge';

const NATIVE_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * FounderPushToggle
 * ─────────────────────────────────────────────────────────────────
 * One-tap control for the founder to enable / disable Web Push so
 * the platform can ring their lock screen the instant a form is
 * submitted. Renders inline (pill-shaped) inside the Founder Inbox
 * header.
 *
 * iOS quirk: Apple requires the site to be added to the Home Screen
 * AND opened from there before the permission prompt is even legal.
 * We detect that and show a tappable "Add to Home Screen" hint when
 * the founder is in Safari instead of in the installed PWA.
 *
 * Props:
 *   userId — the founder's public user_id. Critical — without it
 *            notify_founders_push() can't match the device to the
 *            founder email and won't fire.
 */
export default function FounderPushToggle({ userId }) {
  const [state, setState] = useState('checking'); // checking | off | on | needs-pwa | unsupported | busy
  const [error, setError] = useState('');

  // Initial diagnosis
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isPushSupported()) { if (!cancelled) setState('unsupported'); return; }
      if (isIOS() && !isStandalonePWA()) {
        if (!cancelled) setState('needs-pwa');
        return;
      }
      const sub = await getCurrentSubscription();
      if (cancelled) return;
      if (sub && Notification.permission === 'granted') setState('on');
      else setState('off');
    })();
    return () => { cancelled = true; };
  }, []);

  const enable = async () => {
    setError('');
    setState('busy');
    if (isNativeApp()) {
      const ok = await requestNativePush(NATIVE_API);
      if (ok) {
        setState('on');
        toast.success('Founder push alerts on');
      } else {
        setState('off');
        setError('Notifications are off for this app — enable them in iOS Settings → FLYT League → Notifications.');
      }
      return;
    }
    try {
      await subscribeForPush({ userId });
      setState('on');
      try {
        // Local confirmation ping so the user can verify it's actually working.
        new Notification('Founder alerts on', {
          body: "We'll ping you the moment a new form comes in.",
          icon: '/icons/icon-192.png',
        });
      } catch { /* not critical */ }
      toast.success('Founder push alerts on');
    } catch (e) {
      const msg = e?.message || 'Could not enable.';
      if (msg === 'IOS_NEEDS_PWA') {
        setState('needs-pwa');
        return;
      }
      if (msg === 'PERMISSION_DENIED') {
        setError("Browser denied notifications — re-enable in iOS Settings → Notifications → Dreams League.");
      } else {
        setError(msg);
      }
      setState('off');
      toast.error('Could not enable push');
    }
  };

  const disable = async () => {
    if (isNativeApp()) {
      toast.info('Manage alerts in iOS Settings → FLYT League → Notifications.');
      return;
    }
    setState('busy');
    try {
      await unsubscribeFromPush();
      setState('off');
      toast.success('Founder alerts paused');
    } catch (_) {
      setState('on');
      toast.error('Could not unsubscribe');
    }
  };

  if (state === 'unsupported') return null;

  if (state === 'needs-pwa') {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-500/15 text-blue-200 ring-1 ring-blue-300/30 text-[11px] font-bold"
        title="Add this site to your Home Screen, then open it from there to enable iOS push."
        data-testid="founder-push-needs-pwa"
      >
        <Smartphone className="w-3.5 h-3.5" />
        <span>Add to Home Screen for alerts</span>
      </span>
    );
  }

  if (state === 'on') {
    return (
      <button
        type="button"
        onClick={disable}
        className="inline-flex items-center gap-1 px-2.5 sm:px-3 py-1.5 rounded-full bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/30 text-[11px] font-bold hover:bg-emerald-500/25 transition whitespace-nowrap"
        data-testid="founder-push-on"
        title="Founder push alerts are ON — tap to pause"
      >
        <BellRing className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Alerts on</span>
        <Check className="w-3 h-3" />
      </button>
    );
  }

  return (
    <span className="inline-flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={enable}
        disabled={state === 'busy'}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-yellow-300 text-black text-[11px] font-bold hover:bg-yellow-200 transition active:scale-95 disabled:opacity-70"
        data-testid="founder-push-enable"
      >
        {state === 'busy'
          ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Enabling…</>
          : <><Bell className="w-3.5 h-3.5" />Enable lock-screen alerts</>
        }
      </button>
      {error && (
        <span className="text-[10px] text-rose-300/90 max-w-[220px] text-right" data-testid="founder-push-error">
          {error}
        </span>
      )}
    </span>
  );
}

/** Small icon-only variant for tight spots (eg. the live widget header). */
export function FounderPushIcon({ userId }) {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let c = false;
    (async () => {
      if (isNativeApp()) {
        const status = await getNativePushStatus();
        if (!c) setOn(status === 'granted');
        return;
      }
      if (!isPushSupported()) return;
      const sub = await getCurrentSubscription();
      if (!c) setOn(!!sub && Notification.permission === 'granted');
    })();
    return () => { c = true; };
  }, []);
  const tap = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (isNativeApp()) {
        if (on) {
          toast.info('Manage alerts in iOS Settings → FLYT League → Notifications.');
        } else {
          const ok = await requestNativePush(NATIVE_API);
          setOn(ok);
          if (ok) toast.success('Founder alerts on');
          else toast.info('Enable notifications in iOS Settings → FLYT League.');
        }
        return;
      }
      if (on) {
        await unsubscribeFromPush();
        setOn(false);
      } else {
        await subscribeForPush({ userId });
        setOn(true);
        toast.success('Founder alerts on');
      }
    } catch (e) {
      const msg = e?.message || 'Could not toggle';
      toast.error(msg === 'PERMISSION_DENIED' ? 'Permission denied' : 'Push toggle failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={tap}
      className={`p-1 rounded-full hover:bg-white/10 ${on ? 'text-emerald-300' : 'text-yellow-300'}`}
      aria-label={on ? 'Disable push' : 'Enable push'}
      data-testid="founder-live-push-toggle"
      title={on ? 'Push on (tap to pause)' : 'Enable lock-screen alerts'}
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : on ? <BellRing className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
    </button>
  );
}
